import { Form, useFormikContext } from 'formik';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FC, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectIdentityCompanyId } from '../../store/entities/identity';
import { useBusinessSettings } from '../../hooks/useBusinessSettings';
import { FormContainer } from '../common/form/FormContainer';
import { SubmitButton } from '../common/form/SubmitButton';
import * as Yup from 'yup';
import { validations } from '../../utils/validations';
import { getCompanySettings, selectCompanySettings, selectCompanySettingsState } from '../../store/entities/companySettings';
import { Box, Divider, Switch, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Input } from '../common/form/Input';
import { QcsFormControlLabel } from '../common/basic/QcsFormControlLabel';
import { CompanySettingsDto } from '@api/models/settings/CompanySettingsDto';
import { companySettingsService } from '@api/services/services';
import { ShowError } from '../ShowError';
import { ShowResourceContent } from '../ShowResourceContent';
import { resources } from '@api/resources';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { formatChangeInfo } from '../../utils/format';
import { ShowMoreToggle } from '../common/form/ShowMoreToggle';
import { SelectMultiple } from '../common/form/SelectMultiple';
import { QcsSwitch } from '../common/form/QcsSwitch';
import { ConditionalTimeInput } from '../common/form/ConditionalTimeInput';
import { InvitationState } from '@api/models/enums/InvitationState';
import { UnresolvedInvitationReminderRequest } from '@api/models/settings/UnresolvedInvitationReminderRequest';

type SmtpType = 'S4E' | 'custom';

const HISTORY_COLLAPSED_COUNT = 1;

const REMINDER_FIELD = 'unresolvedInvitationReminder';

// An archived invitation is already resolved, so it can never trigger a reminder.
const REMINDABLE_INVITATION_STATES = Object.values(InvitationState).filter((state) => state !== InvitationState.Archived);

// The interval is left to ConditionalTimeInput, the rest of the object has to be seeded alongside it.
const DEFAULT_REMINDER: Omit<UnresolvedInvitationReminderRequest, 'intervalDays'> = {
  invitationStates: [InvitationState.Sent, InvitationState.Viewed],
  copyToResponsiblePerson: false,
};

const UnresolvedInvitationReminderSection: FC = () => {
  const { t, i18n } = useTranslation();
  const { values, setFieldValue } = useFormikContext<CompanySettingsDto>();

  const reminder = values.unresolvedInvitationReminder;

  const getStateName = (state: string) => t(`invitation.states.${state}`);

  // The interval toggle drives the whole reminder, so it also has to seed and clear the other fields.
  const handleToggle = (enabled: boolean) => {
    if (!enabled) {
      setFieldValue(REMINDER_FIELD, null);
      return;
    }

    setFieldValue(`${REMINDER_FIELD}.invitationStates`, DEFAULT_REMINDER.invitationStates);
    setFieldValue(`${REMINDER_FIELD}.copyToResponsiblePerson`, DEFAULT_REMINDER.copyToResponsiblePerson);
  };

  const stateNames = new Intl.ListFormat(i18n.language, { type: 'disjunction' }).format((reminder?.invitationStates ?? []).map(getStateName));

  return (
    <ConditionalTimeInput
      fieldName={`${REMINDER_FIELD}.intervalDays`}
      titleKey="qcsCompanySettings.unresolvedInvitationReminder.title"
      timeInputLabelKey="qcsCompanySettings.unresolvedInvitationReminder.intervalDays"
      outputUnit="days"
      forcedUnit="days"
      outputFormat="number"
      onToggle={handleToggle}
    >
      <SelectMultiple
        name={`${REMINDER_FIELD}.invitationStates`}
        label={t('qcsCompanySettings.unresolvedInvitationReminder.invitationStates')}
        options={REMINDABLE_INVITATION_STATES}
        getOptionId={(option: InvitationState) => option}
        getOptionNameFromId={getStateName}
        required
        fullWidth
      />

      <QcsSwitch
        name={`${REMINDER_FIELD}.copyToResponsiblePerson`}
        label={t('qcsCompanySettings.unresolvedInvitationReminder.copyToResponsiblePerson')}
      />

      {!!reminder?.invitationStates.length && !!reminder.intervalDays && (
        <Typography variant="body2" color="text.secondary">
          {t('qcsCompanySettings.unresolvedInvitationReminder.example', {
            days: reminder.intervalDays,
            states: stateNames,
          })}
        </Typography>
      )}
    </ConditionalTimeInput>
  );
};

const SmsNotificationsSection: FC = () => {
  const { t } = useTranslation();
  const businessSettings = useBusinessSettings();
  const { values, setFieldValue } = useFormikContext<CompanySettingsDto>();
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showFullHistory, setShowFullHistory] = useState(false);

  const history = [...values.smsBillingConsentHistory].reverse();
  const visibleHistory = showFullHistory ? history : history.slice(0, HISTORY_COLLAPSED_COUNT);

  const handleToggle = (_: React.SyntheticEvent, checked: boolean) => {
    if (checked) {
      setShowConsentModal(true);
      return;
    }
    setFieldValue('smsBillingConsent', false);
  };

  const handleConfirmConsent = () => {
    setFieldValue('smsBillingConsent', true);
  };

  return (
    <>
      <QcsFormControlLabel
        control={<Switch checked={!!values.smsBillingConsent} onChange={handleToggle} />}
        label={t('qcsCompanySettings.enableSmsNotifications')}
      />

      <ConfirmDialog
        onClose={() => setShowConsentModal(false)}
        onConfirm={showConsentModal ? handleConfirmConsent : null}
        title={t('qcsCompanySettings.smsConsent.title')}
        message={
          <>
            <Trans i18nKey="qcsCompanySettings.smsConsent.message" values={{ company: businessSettings?.companyName }} />
            <br />
            <br />
            {t('qcsCompanySettings.smsConsent.declaration')}
          </>
        }
        confirmMessage={t('qcsCompanySettings.smsConsent.cancel')}
        cancelText={t('qcsCompanySettings.smsConsent.confirm')}
      />

      {history.length > 0 && (
        <Box mt={2}>
          <Typography variant="subtitle2" fontWeight={600} mb={1}>
            {t('qcsCompanySettings.smsConsent.historyTitle')}
          </Typography>

          {visibleHistory.map((change, index) => (
            <Typography key={index} variant="body2" color="text.secondary">
              {change.value === 'true' ? t('qcsCompanySettings.smsConsent.historyEnabled') : t('qcsCompanySettings.smsConsent.historyDisabled')}{' '}
              &mdash; {formatChangeInfo(change)}
            </Typography>
          ))}

          {history.length > HISTORY_COLLAPSED_COUNT && (
            <Box mt={1}>
              <ShowMoreToggle expanded={showFullHistory} onToggle={() => setShowFullHistory((prev) => !prev)} />
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

interface NotificationSettingsFormProps {
  settings: CompanySettingsDto;
}

// Rendered only once the settings are loaded, so the initial smtpType is derived from real data.
const NotificationSettingsForm: FC<NotificationSettingsFormProps> = ({ settings }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // Determine if custom SMTP is being used based on filled fields
  const hasCustomSmtp = Boolean(settings.smtpHost || settings.smtpPort || settings.smtpUsername || settings.smtpPassword || settings.sender);

  const [smtpType, setSmtpType] = useState<SmtpType>(hasCustomSmtp ? 'custom' : 'S4E');

  const handleSmtpTypeChange = (_event: React.MouseEvent<HTMLElement>, newValue: SmtpType | null) => {
    if (newValue == null) return;
    setSmtpType(newValue);
  };

  const getValidationSchema = () => {
    // Nested shape is only validated when the reminder is enabled (i.e. the value is not null).
    const reminderShape = {
      [REMINDER_FIELD]: Yup.object()
        .nullable()
        .default(null)
        .shape({
          intervalDays: validations.intPositive(t),
          invitationStates: validations.arrayNotEmpty(t),
        }),
    };

    if (smtpType === 'custom') {
      return Yup.object().shape({
        smtpHost: validations.stringRequired(t),
        smtpPort: validations.intPositive(t),
        smtpUsername: validations.stringRequired(t),
        smtpPassword: validations.stringRequired(t),
        sender: validations.emailRequired(t),
        ...reminderShape,
      });
    }
    return Yup.object().shape(reminderShape);
  };

  const handleSubmit = async (data: CompanySettingsDto) => {
    const submissionData: CompanySettingsDto = {
      ...data,
      // If switching to Safe4Entry SMTP, clear all custom SMTP fields
      ...(smtpType === 'S4E' && {
        smtpHost: undefined,
        smtpPort: undefined,
        smtpUsername: undefined,
        smtpPassword: undefined,
        sender: undefined,
      }),
    };

    await companySettingsService.updateSettings(submissionData);
    dispatch(getCompanySettings());
  };

  return (
    <FormikDebounce<CompanySettingsDto> initialValues={settings} validationSchema={getValidationSchema()} onSubmit={handleSubmit}>
      <Form>
        <FormContainer>
          <Typography variant="h5" fontWeight={600} mb={1}>
            {t('qcsCompanySettings.emailNotifications')}
          </Typography>

          <Box mb={2}>
            <ToggleButtonGroup value={smtpType} exclusive onChange={handleSmtpTypeChange} fullWidth>
              <ToggleButton value="S4E" sx={{ textTransform: 'none' }}>
                {t('qcsCompanySettings.useS4ESmtp')}
              </ToggleButton>
              <ToggleButton value="custom" sx={{ textTransform: 'none' }}>
                {t('qcsCompanySettings.useCustomSmtp')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {smtpType === 'custom' && (
            <>
              <Input name="smtpHost" label={t('qcsCompanySettings.smtpHost')} required />
              <Input name="smtpPort" label={t('qcsCompanySettings.smtpPort')} type="number" required />
              <Input name="smtpUsername" label={t('qcsCompanySettings.smtpUsername')} required />
              <Input name="smtpPassword" type="password" label={t('qcsCompanySettings.smtpPassword')} required autoComplete="off" />
              <Input name="sender" label={t('qcsCompanySettings.sender')} required />
            </>
          )}

          <UnresolvedInvitationReminderSection />

          <Divider sx={{ my: 3 }} />

          <Typography variant="h5" fontWeight={600} mb={1}>
            {t('qcsCompanySettings.smsNotifications')}
          </Typography>

          <SmsNotificationsSection />

          <ShowError resource={resources.updateSettings} />
          <SubmitButton />
        </FormContainer>
      </Form>
    </FormikDebounce>
  );
};

export const CompSettTabNotification: FC = () => {
  const dispatch = useAppDispatch();
  const identityCompanyId = useAppSelector(selectIdentityCompanyId);
  const companySettings = useAppSelector(selectCompanySettings);

  useEffect(() => {
    dispatch(getCompanySettings());
  }, [dispatch, identityCompanyId]);

  return (
    <Box mt={2}>
      <ShowResourceContent resource={resources.getSettings} fetchStateSelector={selectCompanySettingsState}>
        <NotificationSettingsForm settings={companySettings} />
      </ShowResourceContent>
    </Box>
  );
};
